import { AuthProvider } from "@ozpanel/ui";
import { Gate } from "./Gate";
import { createBrowserRouter, RouterProvider } from "react-router";
import { AppShell } from "./AppShell";
import { DashboardScreen } from "./screens/DashboardScreen";
import { ModulesScreen } from "./screens/ModulesScreen";
import { ModuleDetailScreen } from "./screens/ModuleDetailScreen";
import { JobsScreen } from "./screens/JobsScreen";
import { PlansScreen } from "./screens/accounts/PlansScreen";
import { TenantsScreen } from "./screens/accounts/TenantsScreen";
import { TenantDetailScreen } from "./screens/accounts/TenantDetailScreen";
import { AppsScreen } from "./screens/accounts/AppsScreen";
import { AppDetailScreen } from "./screens/accounts/AppDetailScreen";
import { UsageScreen } from "./screens/accounts/UsageScreen";
import { BackupsScreen } from "./screens/accounts/BackupsScreen";
import { DomainsScreen } from "./screens/web/DomainsScreen";
import { NotificationsScreen } from "./screens/NotificationsScreen";
import { OpenAlertsScreen } from "./screens/OpenAlertsScreen";
import { SecurityScreen } from "./screens/SecurityScreen";
import { ConfigCheckScreen } from "./screens/ConfigCheckScreen";
import { PlatformScreen } from "./screens/PlatformScreen";
import { RelaysScreen } from "./screens/mail/RelaysScreen";
import { PlatformSenderScreen } from "./screens/mail/PlatformSenderScreen";
import { MailDomainsScreen } from "./screens/mail/MailDomainsScreen";
import { MailOverviewScreen } from "./screens/mail/MailOverviewScreen";
import { FirewallScreen } from "./screens/network/FirewallScreen";
import { PortRequestsScreen } from "./screens/network/PortRequestsScreen";
import { RedisRequestsScreen } from "./screens/network/RedisRequestsScreen";
import { TokenRequestsScreen } from "./screens/network/TokenRequestsScreen";
import { ApiTokensScreen } from "./screens/network/ApiTokensScreen";
import { VhostsScreen } from "./screens/web/VhostsScreen";
import { VhostDetailScreen } from "./screens/web/VhostDetailScreen";
import { DnsZonesScreen, DnsZoneDetailScreen } from "./screens/web/DnsZonesScreen";
import { StorageScreen } from "./screens/nodes/StorageScreen";
import { AppEventsScreen, DatabaseScreen } from "./screens/PlatformOpsScreens";
import { PlaneMatrixScreen } from "./screens/nodes/PlaneMatrixScreen";
import { AddNodeScreen } from "./screens/nodes/AddNodeScreen";
import { UpdatesScreen } from "./screens/nodes/UpdatesScreen";
import { MailDomainDetailScreen } from "./screens/mail/MailDomainDetailScreen";
import { TunablesScreen } from "./screens/TunablesScreen";
import { RecoveryScreen } from "./screens/RecoveryScreen";
import { InstallScreen } from "./screens/InstallScreen";

const router = createBrowserRouter(
  [
    {
      path: "/",
      element: <AppShell />,
      children: [
        { index: true, element: <DashboardScreen /> },
        { path: "modules", element: <ModulesScreen /> },
        { path: "modules/:name", element: <ModuleDetailScreen /> },
        { path: "jobs", element: <JobsScreen /> },
        { path: "tenants", element: <TenantsScreen /> },
        { path: "plans", element: <PlansScreen /> },
        { path: "tenants/:id", element: <TenantDetailScreen /> },
        { path: "apps", element: <AppsScreen /> },
        { path: "apps/:name", element: <AppDetailScreen /> },
        { path: "modules/usage", element: <UsageScreen /> },
        { path: "modules/backups", element: <BackupsScreen /> },
      { path: "domains", element: <DomainsScreen /> },
      { path: "modules/notify", element: <NotificationsScreen /> },
      { path: "notifications", element: <NotificationsScreen /> },
      { path: "alerts", element: <OpenAlertsScreen /> },
      { path: "security", element: <SecurityScreen /> },
      { path: "config-check", element: <ConfigCheckScreen /> },
      { path: "platform", element: <PlatformScreen /> },
      { path: "mail/relays", element: <RelaysScreen /> },
      { path: "mail/platform-sender", element: <PlatformSenderScreen /> },
      { path: "mail", element: <MailOverviewScreen /> },
      { path: "firewall", element: <FirewallScreen /> },
      { path: "port-requests", element: <PortRequestsScreen /> },
      { path: "redis-requests", element: <RedisRequestsScreen /> },
      { path: "token-requests", element: <TokenRequestsScreen /> },
      { path: "api-tokens", element: <ApiTokensScreen /> },
      { path: "web", element: <VhostsScreen /> },
      { path: "web/:domain", element: <VhostDetailScreen /> },
      { path: "storage", element: <StorageScreen /> },
      { path: "app-events", element: <AppEventsScreen /> },
      { path: "database", element: <DatabaseScreen /> },
      { path: "dns/zones", element: <DnsZonesScreen /> },
      { path: "dns/zones/:zone", element: <DnsZoneDetailScreen /> },
      { path: "updates", element: <UpdatesScreen /> },
      // The transfer watch as its OWN route, so it can be popped out into a second window and left
      // open through a failover while the main console is used for the work being watched.
      { path: "nodes/planes", element: <PlaneMatrixScreen /> },
      // Adding a node: preflight, join from the master, watch both halves (AddNodeScreen.tsx).
      { path: "nodes/add", element: <AddNodeScreen /> },
      { path: "mail/domains", element: <MailDomainsScreen /> },
      { path: "mail/domains/:domain", element: <MailDomainDetailScreen /> },
      { path: "tunables", element: <TunablesScreen /> },
      ],
    },
  ],
  // import.meta.env.BASE_URL mirrors vite.config.ts's `base` (OZPANEL_BASE_PATH) — "/" for
  // local dev, "/panel-v2-preview/" when built for the server.go preview mount, so every
  // client-side link/navigate resolves under whichever path this build is actually served at.
  { basename: import.meta.env.BASE_URL },
);

// Recovery mode lives OUTSIDE the session gate: it is for a node whose panel has no database, so no
// session can be had there. It authenticates with the node's recovery token instead.
// The install screen likewise: a node being installed has no login yet.
const onPath = (p: string) => window.location.pathname.replace(/\/+$/, "") === `${import.meta.env.BASE_URL}${p}`;

export function App() {
  if (onPath("recovery")) {
    return <RecoveryScreen />;
  }
  if (onPath("install")) {
    return <InstallScreen />;
  }
  return (
    <AuthProvider>
      <Gate>
        <RouterProvider router={router} />
      </Gate>
    </AuthProvider>
  );
}
